import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ChevronDown, ChevronUp, Check, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const WarrantySection = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get warranty badge text or default
  const warrantyBadge = product?.badges?.warranty || '5-Year Warranty';
  const warrantyInfo = product?.warranty_info;

  // Default warranty highlights if none specified
  const defaultHighlights = [
    'Coverage against manufacturer defects',
    'Rebuilds or replacement included',
    'Dedicated customer support'
  ];

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 overflow-hidden" data-testid="warranty-section">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-emerald-50/50 transition-colors"
        data-testid="warranty-section-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-[#0a0203]">{warrantyBadge}</h3>
            <p className="text-sm text-gray-500">Click to see warranty details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-emerald-600 text-sm font-medium hidden sm:block">View Details</span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-emerald-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-emerald-600" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-emerald-200 pt-4 animate-fade-in">
          {/* Custom warranty info from admin */}
          {warrantyInfo && (
            <div className="bg-white rounded-lg p-4 mb-4 border border-emerald-100">
              <p className="text-gray-700 whitespace-pre-line">{warrantyInfo}</p>
            </div>
          )}

          {/* Default highlights if no custom info */}
          {!warrantyInfo && (
            <div className="bg-white rounded-lg p-4 mb-4 border border-emerald-100">
              <h4 className="font-semibold text-[#0a0203] mb-3">Warranty Highlights</h4>
              <ul className="space-y-2">
                {defaultHighlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-600">
                    <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-emerald-600" />
                    </div>
                    <span className="text-sm">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warranty coverage summary */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
              <p className="text-2xl font-bold text-emerald-600">100%</p>
              <p className="text-xs text-gray-500">Parts Covered</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
              <p className="text-2xl font-bold text-emerald-600">100%</p>
              <p className="text-xs text-gray-500">Labor Covered</p>
            </div>
          </div>

          {/* CTA to full warranty page */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Link to="/warranty" className="flex-1">
              <Button 
                variant="outline" 
                className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                data-testid="warranty-full-details-btn"
              >
                <Shield className="h-4 w-4 mr-2" />
                Full Warranty Terms
                <ExternalLink className="h-3 w-3 ml-2" />
              </Button>
            </Link>
            <Link to="/contact" className="flex-1">
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="warranty-claim-btn"
              >
                Start Warranty Claim
              </Button>
            </Link>
          </div>

          <p className="text-xs text-gray-500 text-center mt-3">
            Register your product within 30 days of purchase for extended coverage.
          </p>
        </div>
      )}
    </div>
  );
};

export default WarrantySection;
